import React, { useState, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Plus, Trash2, AlertTriangle, Target, Pencil, TrendingUp, Wallet, PieChart } from "lucide-react";
import { CATEGORY_LABELS, CASH_OUT_CATEGORIES, formatAmount } from "@shared/types";
import type { TransactionCategory } from "@shared/types";
import { useLanguage, CATEGORY_LABELS_AR } from "@/contexts/LanguageContext";
import { Fragment } from "react";
import { RouteFeedback } from "@/components/RouteFeedback";

export default function Budgets() {
  const { t, language, isRTL } = useLanguage();
  const getCategoryLabel = (cat: string) => {
    const c = cat === "bills_utilities" ? "bills" : cat;
    return language === "ar"
      ? (CATEGORY_LABELS_AR[c] ?? CATEGORY_LABELS[c as TransactionCategory] ?? c.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()))
      : (CATEGORY_LABELS[c as TransactionCategory] ?? c.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()));
  };

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ category: "", limitAmount: "", period: "monthly" as "weekly" | "monthly" });
  const [editingBudget, setEditingBudget] = useState<any>(null);
  const [editForm, setEditForm] = useState({ limitAmount: "", period: "monthly" as "weekly" | "monthly" });

  // Queries
  const budgetSpendingQuery = trpc.budget.spending.useQuery();
  const alertsQuery = trpc.budget.alerts.useQuery();
  const categoriesQuery = trpc.budget.categories.useQuery();
  const budgetsQuery = trpc.budget.list.useQuery();
  const { data: budgetSpending } = budgetSpendingQuery;
  const { data: alerts } = alertsQuery;
  const { data: userCategories } = categoriesQuery;
  const { data: budgets } = budgetsQuery;
  const pageQueries = [budgetSpendingQuery, alertsQuery, categoriesQuery, budgetsQuery];
  const isPageLoading = pageQueries.some((query) => query.isLoading);
  const isPageError = pageQueries.some((query) => query.isError);
  const retryPageQueries = () => { pageQueries.forEach((query) => { void query.refetch(); }); };
  const utils = trpc.useUtils();

  // Categories that already have budgets
  const budgetedCategories = useMemo(() => {
    return new Set(budgets?.map((b) => b.category) ?? []);
  }, [budgets]);

  // Available categories for new budget
  const availableCategories = useMemo(() => {
    const userCats = (userCategories ?? [])
      .filter((c) => !budgetedCategories.has(c.category))
      .map((c) => c.category);
    const remainingCats = CASH_OUT_CATEGORIES.filter(
      (cat) => !budgetedCategories.has(cat) && !userCats.includes(cat)
    );
    return { userCats, remainingCats };
  }, [userCategories, budgetedCategories]);

  const createMutation = trpc.budget.create.useMutation({
    onSuccess: () => {
      toast.success(language === "ar" ? "تم إنشاء الميزانية" : "Budget created");
      setShowCreate(false);
      setCreateForm({ category: "", limitAmount: "", period: "monthly" });
      utils.budget.list.invalidate();
      utils.budget.alerts.invalidate();
      utils.budget.spending.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const updateMutation = trpc.budget.update.useMutation({
    onSuccess: () => {
      toast.success(language === "ar" ? "تم تحديث الميزانية" : "Budget updated");
      setEditingBudget(null);
      utils.budget.list.invalidate();
      utils.budget.alerts.invalidate();
      utils.budget.spending.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const deleteMutation = trpc.budget.delete.useMutation({
    onSuccess: () => {
      toast.success(language === "ar" ? "تم حذف الميزانية" : "Budget deleted");
      utils.budget.list.invalidate();
      utils.budget.alerts.invalidate();
      utils.budget.spending.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  // Summary stats
  const totalBudget = budgetSpending?.reduce((sum, b) => sum + parseFloat(b.limitAmount), 0) ?? 0;
  const totalSpent = budgetSpending?.reduce((sum, b) => sum + b.spent, 0) ?? 0;
  const totalRemaining = totalBudget - totalSpent;
  const overallPercentage = totalBudget > 0 ? Math.round((totalSpent / totalBudget) * 100) : 0;

  const getProgressColor = (pct: number) => {
    if (pct >= 100) return "text-red-500";
    if (pct >= 80) return "text-amber-500";
    return "text-emerald-500";
  };

  const getProgressBarClass = (pct: number) => {
    if (pct >= 100) return "[&>div]:bg-red-500";
    if (pct >= 80) return "[&>div]:bg-amber-500";
    return "[&>div]:bg-emerald-500";
  };

  if (isPageLoading || isPageError) {
    return <div dir={isRTL ? "rtl" : "ltr"}><RouteFeedback isLoading={isPageLoading} isError={isPageError} onRetry={retryPageQueries} label={t("budgets.title")} /></div>;
  }

  return (
    <div className="space-y-5 sm:space-y-6" dir={isRTL ? "rtl" : "ltr"}>
      {/* Header */}
      <div className="flex items-start sm:items-center justify-between gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-foreground">{t("budgets.title")}</h1>
          <p className="text-muted-foreground text-xs sm:text-sm mt-0.5 sm:mt-1">
            {t("budgets.subtitle")}
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)} size="sm" className="gap-1.5 sm:gap-2 text-xs sm:text-sm flex-shrink-0">
          <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
          {language === "ar" ? "إضافة ميزانية" : "Add Budget"}
        </Button>
      </div>

      {/* ═══════════════════════════════════════════════════════ */}
      {/* SECTION 1: Summary Overview — compact inline stats     */}
      {/* Uses a gradient background to stand out as the header  */}
      {/* ═══════════════════════════════════════════════════════ */}
      {budgetSpending && budgetSpending.length > 0 && (
        <div className="rounded-xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/20 p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-7 w-7 rounded-lg bg-primary/20 flex items-center justify-center">
              <PieChart className="h-4 w-4 text-primary" />
            </div>
            <h2 className="text-sm font-semibold text-foreground">
              {language === "ar" ? "ملخص الميزانية" : "Budget Summary"}
            </h2>
          </div>
          <div className="grid grid-cols-3 gap-3 sm:gap-4">
            <div className="text-center p-2.5 sm:p-3 rounded-lg bg-background/60 backdrop-blur-sm">
              <p className="text-[10px] sm:text-xs text-muted-foreground mb-0.5">{language === "ar" ? "إجمالي الميزانية" : "Total Budget"}</p>
              <p className="text-xs sm:text-lg font-bold text-foreground whitespace-nowrap">{formatAmount(totalBudget)}</p>
            </div>
            <div className="text-center p-2.5 sm:p-3 rounded-lg bg-background/60 backdrop-blur-sm">
              <p className="text-[10px] sm:text-xs text-muted-foreground mb-0.5">{language === "ar" ? "إجمالي المصروف" : "Total Spent"}</p>
              <p className="text-xs sm:text-lg font-bold text-red-500 whitespace-nowrap">{formatAmount(totalSpent)}</p>
            </div>
            <div className="text-center p-2.5 sm:p-3 rounded-lg bg-background/60 backdrop-blur-sm">
              <p className="text-[10px] sm:text-xs text-muted-foreground mb-0.5">{language === "ar" ? "المتبقي" : "Remaining"}</p>
              <p className={`text-xs sm:text-lg font-bold whitespace-nowrap ${totalRemaining >= 0 ? "text-emerald-500" : "text-red-500"}`}>
                {totalRemaining < 0 && "-"}{formatAmount(Math.abs(totalRemaining))}
              </p>
            </div>
          </div>
          {/* Overall progress bar integrated into summary */}
          <div className="mt-3 pt-3 border-t border-primary/10">
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-xs text-muted-foreground">
                {language === "ar" ? "الاستخدام الإجمالي" : "Overall Usage"}
              </p>
              <span className={`text-xs font-bold ${getProgressColor(overallPercentage)}`}>
                {overallPercentage}%
              </span>
            </div>
            <Progress value={Math.min(overallPercentage, 100)} className={`h-2.5 ${getProgressBarClass(overallPercentage)}`} />
            <p className="text-[10px] text-muted-foreground mt-1">
              {formatAmount(totalSpent)} {language === "ar" ? "من" : "of"} {formatAmount(totalBudget)} {language === "ar" ? "مستخدم" : "used"}
            </p>
          </div>
        </div>
      )}

      {/* Active Alerts */}
      {alerts && alerts.length > 0 && (
        <div className="rounded-xl border-2 border-amber-400/50 dark:border-amber-500/30 bg-amber-50/50 dark:bg-amber-950/20 p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <h2 className="text-sm font-semibold text-amber-700 dark:text-amber-400">
              {language === "ar" ? `تنبيهات نشطة (${alerts.length})` : `Active Alerts (${alerts.length})`}
            </h2>
          </div>
          <div className="space-y-2.5">
            {alerts.map((alert) => (
              <div key={alert.category} className="p-3 rounded-lg bg-amber-100/60 dark:bg-amber-900/20 border border-amber-200/50 dark:border-amber-800/30">
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-sm font-medium text-foreground">
                    {getCategoryLabel(alert.category)}
                  </p>
                  <Badge variant={alert.percentage >= 100 ? "destructive" : "secondary"} className="text-[10px]">
                    {alert.percentage}%
                  </Badge>
                </div>
                <Progress
                  value={Math.min(alert.percentage, 100)}
                  className={`h-1.5 ${getProgressBarClass(alert.percentage)}`}
                />
                <p className="text-[10px] text-muted-foreground mt-1">
                  {formatAmount(alert.spent)} {language === "ar" ? "من" : "of"} {formatAmount(alert.limit)} ({alert.period === "monthly" ? (language === "ar" ? "شهري" : "monthly") : (language === "ar" ? "أسبوعي" : "weekly")})
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════ */}
      {/* SECTION 2: Budget Tracking Cards — individual budgets  */}
      {/* Uses standard Card with left color accent border       */}
      {/* ═══════════════════════════════════════════════════════ */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Target className="h-4 w-4 text-primary" />
          <h2 className="text-sm font-semibold text-foreground">
            {language === "ar" ? "تتبع الميزانيات" : "Budget Tracking"}
          </h2>
          {budgetSpending && budgetSpending.length > 0 && (
            <Badge variant="outline" className="text-[10px]">{budgetSpending.length}</Badge>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {budgetSpending && budgetSpending.length > 0 ? (
            budgetSpending.map((item) => {
              const limit = parseFloat(item.limitAmount);
              const remaining = limit - item.spent;
              const isOver = item.percentage >= 100;
              const isWarning = item.percentage >= 80 && !isOver;

              // Left border color based on status
              const borderColor = isOver
                ? "border-l-red-500"
                : isWarning
                  ? "border-l-amber-500"
                  : "border-l-emerald-500";

              return (
                <Card key={item.budgetId ?? item.category} className={`border-l-4 ${borderColor} ${isOver ? "bg-red-50/30 dark:bg-red-950/10" : ""}`}>
                  <CardContent className="pt-4 pb-3 space-y-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm font-semibold text-foreground">
                          {getCategoryLabel(item.category)}
                        </p>
                        <Badge variant="outline" className="mt-1 text-[10px]">
                          {item.period === "monthly" ? (language === "ar" ? "شهري" : "Monthly") : (language === "ar" ? "أسبوعي" : "Weekly")}
                        </Badge>
                      </div>
                      <div className="flex gap-0.5">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => {
                            setEditingBudget({ id: item.budgetId, category: item.category, limitAmount: item.limitAmount, period: item.period });
                            setEditForm({ limitAmount: item.limitAmount, period: item.period as "weekly" | "monthly" });
                          }}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive hover:text-destructive"
                          onClick={() => {
                            if (confirm(language === "ar" ? "حذف هذه الميزانية؟" : "Delete this budget?")) {
                              deleteMutation.mutate({ id: item.budgetId });
                            }
                          }}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>

                    {/* Spending display */}
                    <div>
                      <div className="flex items-end justify-between mb-1.5">
                        <div>
                          <p className="text-base sm:text-xl font-bold text-foreground whitespace-nowrap">{formatAmount(item.spent)}</p>
                          <p className="text-[10px] text-muted-foreground">{language === "ar" ? "مصروف" : "spent"}</p>
                        </div>
                        <div className={`${isRTL ? "text-left" : "text-right"}`}>
                          <p className="text-xs sm:text-sm font-medium text-muted-foreground whitespace-nowrap">{formatAmount(limit)}</p>
                          <p className="text-[10px] text-muted-foreground">{language === "ar" ? "الحد" : "limit"}</p>
                        </div>
                      </div>
                      <Progress value={Math.min(item.percentage, 100)} className={`h-2 ${getProgressBarClass(item.percentage)}`} />
                      <div className="flex items-center justify-between mt-1">
                        <span className={`text-[10px] font-medium ${getProgressColor(item.percentage)}`}>
                          {item.percentage}% {language === "ar" ? "مستخدم" : "used"}
                        </span>
                        <span className={`text-[10px] ${remaining >= 0 ? "text-muted-foreground" : "text-red-500 font-medium"}`}>
                          {remaining >= 0
                            ? `${formatAmount(remaining)} ${language === "ar" ? "متبقي" : "left"}`
                            : `${formatAmount(Math.abs(remaining))} ${language === "ar" ? "تجاوز" : "over"}`}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          ) : (
            <Card className="col-span-full">
              <CardContent className="pt-6">
                <div className="text-center py-12 text-muted-foreground">
                  <Target className="h-10 w-10 mx-auto mb-3 opacity-50" />
                  <p className="text-sm font-medium">{language === "ar" ? "لم يتم تحديد ميزانيات بعد" : "No budgets set yet"}</p>
                  <p className="text-xs mt-1 max-w-sm mx-auto">
                    {language === "ar"
                      ? "حدد حدود الإنفاق لفئات مثل الطعام والنقل والفواتير. ستحصل على تنبيهات عند اقتراب إنفاقك من الحد."
                      : "Set spending limits for categories like Food, Transport, or Bills. You'll get alerts when your spending approaches the limit."}
                  </p>
                  <Button variant="outline" size="sm" className="mt-4 gap-2" onClick={() => setShowCreate(true)}>
                    <Plus className="h-4 w-4" />
                    {language === "ar" ? "إنشاء ميزانيتك الأولى" : "Create Your First Budget"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════ */}
      {/* SECTION 3: Spending Categories — dashed border, list   */}
      {/* Uses dashed border and different background to stand   */}
      {/* out from the budget tracking cards above               */}
      {/* ═══════════════════════════════════════════════════════ */}
      {userCategories && userCategories.length > 0 && userCategories.filter((c) => !budgetedCategories.has(c.category)).length > 0 && (
        <div className="rounded-xl border-2 border-dashed border-muted-foreground/20 bg-muted/30 p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-1">
            <Wallet className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-sm font-semibold text-foreground">
              {language === "ar" ? "فئات الإنفاق بدون ميزانية" : "Unbudgeted Spending Categories"}
            </h2>
          </div>
          <p className="text-[10px] sm:text-xs text-muted-foreground mb-3">
            {language === "ar"
              ? "فئات من معاملاتك الفعلية هذا الشهر. انقر لإنشاء ميزانية لأي فئة."
              : "Categories from your actual transactions this month. Click to create a budget for any."}
          </p>
          <div className="space-y-1.5">
            {userCategories
              .filter((c) => !budgetedCategories.has(c.category))
              .map((cat) => (
                <div
                  key={cat.category}
                  className="flex items-center justify-between p-2.5 sm:p-3 rounded-lg bg-background/80 hover:bg-background transition-colors border border-border/50"
                >
                  <div>
                    <p className="text-sm font-medium text-foreground">{getCategoryLabel(cat.category)}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {cat.transactionCount} {language === "ar" ? "معاملة" : "transaction"}{cat.transactionCount !== 1 && language !== "ar" ? "s" : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3">
                    <span className="text-xs sm:text-sm font-medium text-foreground">{formatAmount(cat.totalSpent)}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1 text-[10px] sm:text-xs h-6 sm:h-7 px-2"
                      onClick={() => {
                        setCreateForm({ category: cat.category, limitAmount: String(Math.ceil(cat.totalSpent / 10) * 10), period: "monthly" });
                        setShowCreate(true);
                      }}
                    >
                      <Plus className="h-3 w-3" />
                      {language === "ar" ? "ميزانية" : "Budget"}
                    </Button>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("budgets.createBudget")}</DialogTitle>
            <DialogDescription>
              {language === "ar"
                ? "حدد فئة ومبلغ الحد الأقصى لتتبع إنفاقك."
                : "Choose a category and set a spending limit to track your expenses."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t("field.category")}</Label>
              <Select value={createForm.category} onValueChange={(v) => setCreateForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder={language === "ar" ? "اختر فئة" : "Select category"} />
                </SelectTrigger>
                <SelectContent>
                  {availableCategories.userCats.length > 0 && (
                    <Fragment key="active-categories">
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                        {language === "ar" ? "فئاتك النشطة" : "Your Active Categories"}
                      </div>
                      {availableCategories.userCats.map((cat) => {
                        const catData = userCategories?.find((c) => c.category === cat);
                        return (
                          <SelectItem key={cat} value={cat}>
                            {getCategoryLabel(cat)}
                            {catData && (
                              <span className="text-muted-foreground ml-2 text-xs">
                                ({formatAmount(catData.totalSpent)} {language === "ar" ? "هذا الشهر" : "this month"})
                              </span>
                            )}
                          </SelectItem>
                        );
                      })}
                    </Fragment>
                  )}
                  {availableCategories.remainingCats.length > 0 && (
                    <Fragment key="remaining-categories">
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground border-t mt-1 pt-2">
                        {language === "ar" ? "فئات أخرى" : "Other Categories"}
                      </div>
                      {availableCategories.remainingCats.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {getCategoryLabel(cat)}
                        </SelectItem>
                      ))}
                    </Fragment>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{language === "ar" ? "المبلغ المحدد (KWD)" : "Limit Amount (KWD)"}</Label>
              <Input
                type="number"
                step="0.001"
                placeholder={language === "ar" ? "مثال: 100.000" : "e.g., 100.000"}
                value={createForm.limitAmount}
                onChange={(e) => setCreateForm((f) => ({ ...f, limitAmount: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>{language === "ar" ? "الفترة" : "Period"}</Label>
              <Select value={createForm.period} onValueChange={(v) => setCreateForm((f) => ({ ...f, period: v as "weekly" | "monthly" }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">{language === "ar" ? "شهري" : "Monthly"}</SelectItem>
                  <SelectItem value="weekly">{language === "ar" ? "أسبوعي" : "Weekly"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>{t("common.cancel")}</Button>
            <Button
              onClick={() => createMutation.mutate(createForm)}
              disabled={!createForm.category || !createForm.limitAmount || createMutation.isPending}
            >
              {createMutation.isPending ? t("common.loading") : t("budgets.createBudget")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editingBudget} onOpenChange={(open) => !open && setEditingBudget(null)}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {language === "ar" ? "تعديل الميزانية" : "Edit Budget"} — {editingBudget ? getCategoryLabel(editingBudget.category) : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{language === "ar" ? "المبلغ المحدد (KWD)" : "Limit Amount (KWD)"}</Label>
              <Input
                type="number"
                step="0.001"
                value={editForm.limitAmount}
                onChange={(e) => setEditForm((f) => ({ ...f, limitAmount: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>{language === "ar" ? "الفترة" : "Period"}</Label>
              <Select value={editForm.period} onValueChange={(v) => setEditForm((f) => ({ ...f, period: v as "weekly" | "monthly" }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">{language === "ar" ? "شهري" : "Monthly"}</SelectItem>
                  <SelectItem value="weekly">{language === "ar" ? "أسبوعي" : "Weekly"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingBudget(null)}>{t("common.cancel")}</Button>
            <Button
              onClick={() => {
                if (editingBudget) {
                  updateMutation.mutate({
                    id: editingBudget.id,
                    limitAmount: editForm.limitAmount,
                    period: editForm.period,
                  });
                }
              }}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? t("common.loading") : t("transactions.saveChanges")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
